import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import { compose, withProps, withStateHandlers } from 'recompose'
import features from '../../../components/features'
import Button from '../../../components/button/baseButton'
import Loader from '../../../components/loader/Loader'
import TimeEditor from './TimeEditor'
import DateEditor from './DateEditor'
import ServiceEditor from './ServiceEditor'
import StoreEditor from './StoreEditor'
import ServiceProviderEditor from './ServiceProviderEditor'
import {
  WideSectionItem,
  SectionItemHeading,
  SectionItem,
  SectionItemText,
} from '../sharedUI'
import {
  formatCancellationReasonDisplay,
  getOptions,
  lengthFromEndTime,
  endTimeFromLength,
} from '../utils'
import { update, isValid, cancel, hasAppointmentChanges } from './actions'
import useTranslation from '../../../../utils/translation'
import { appBaseUrl } from '../../../../utils/appBaseUrl'
import { MessageModal } from '../../../components/overlay'
import { useHookAsHoc } from '../../../hooks/useHookAsHoc'
import { useCalendar } from '../../../hooks/appointment/useSbmCalendar'
import { useServiceProviders } from '../../../hooks/service-provider/useServiceProviders'

const CancellationReasonText = styled(SectionItemText)`
  white-space: pre-line;
`

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const LoaderCover = styled.div`
  position: absolute;
  left: 0;
  width: 800px;
  height: 200px;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 100000;
  position: relative;
  padding-top: 60px;
`

const ButtonContainer = styled.div`
  width: 100%;
`

const UpdateButton = styled(Button)`
  margin-left: auto;
  display: block;
  width: 399px;
  max-width: 100%;
  margin-top: 0px;
  height: 43px;
  ${(props) => props.theme.media.max.xs`
    width: 100%;
  `};
`

const CancelButton = styled.a`
  display: block;
  text-align: center;
  color: red;
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: 12px;
  text-decoration: underline;
  margin-top: 10px;
  cursor: pointer;
`

const Canceled = styled.div`
  font-weight: 600;
  font-family: ${theme('typography.paragraph.fontFamily')};
  text-align: center;
  width: 100%;
  background-color: #f6f7f8;
  padding: 20px;
  margin-top: 20px;
`

const Error = styled.div`
  background-color: red;
  color: white;
  padding: 10px;
  width: 100%;
  text-align: center;
  margin-top: 15px;
  border-radius: 2px;
`

const getAvailable = (serviceProviderOptions, serviceProviderId) => {
  const designer = serviceProviderOptions.find(
    (option) => `${option.value}` === `${serviceProviderId}`
  )
  return !!designer && designer.disabled !== true
}

const MeetingEditor = ({
  onUpdate,
  canceled,
  calendarVisible,
  toggleCalendarVisible,
  selectedDate,
  storeOptions,
  timeOptions,
  lengthOptions,
  services,
  serviceOptions,
  serviceProviderOptions,
  closed,
  storeId,
  serviceProviderId,
  serviceProviderName,
  appointmentId,
  formData,
  setFormData,
  resetOldFormData,
  onCancel,
  meetingAllowUpdate,
  meetingAllowCancel,
  designerSelectedByCustomer,
  cancelledBy,
  cancellationReason,
}) => {
  const t = useTranslation('appointment-details')
  const tCancelStep2 = useTranslation('cancel.step2')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [selectedDesignerId, setSelectedDesignerId] =
    useState(serviceProviderId)

  useEffect(() => {
    if (storeOptions.length > 0 && serviceProviderOptions.length > 0) {
      setLoading(false)
    }
  }, [storeOptions, serviceProviderOptions])

  const isAvailable = useMemo(
    () => getAvailable(serviceProviderOptions, selectedDesignerId),
    [serviceProviderOptions, selectedDesignerId]
  )

  const providerOptions = useMemo(() => {
    if (!designerSelectedByCustomer) {
      return serviceProviderOptions
    }

    return serviceProviderOptions.map((o) => ({
      ...o,
      label:
        o.value === serviceProviderId
          ? `${o.label} - ${t('selected-by-customer')}`
          : o.label,
    }))
  }, [designerSelectedByCustomer, serviceProviderOptions, serviceProviderId])

  const formattedCancellationReason = useMemo(
    () =>
      formatCancellationReasonDisplay(
        cancellationReason,
        cancelledBy,
        tCancelStep2
      ),
    [cancellationReason, cancelledBy, tCancelStep2]
  )

  if (loading) {
    return (
      <LoaderContainer>
        <LoaderCover>
          <Loader duration={3} />
        </LoaderCover>
      </LoaderContainer>
    )
  }

  return (
    <>
      <DateEditor
        available={isAvailable && meetingAllowUpdate}
        canceled={canceled}
        selectedDate={selectedDate}
        newSelectedDate={formData.newSelectedDate}
        calendarVisible={calendarVisible}
        toggleCalendarVisible={toggleCalendarVisible}
        onChange={(date) =>
          setFormData({
            newSelectedDate: moment(date).format('YYYY-MM-DD'),
          })
        }
      />
      <StoreEditor
        canceled
        storeId={parseInt(storeId, 10)}
        serviceProviderId={parseInt(serviceProviderId, 10)}
        storeOptions={storeOptions}
        serviceProviderOptions={providerOptions}
        onChange={(e) =>
          setFormData({
            newStoreId: parseInt(e.target.value, 10),
          })
        }
      />
      <ServiceProviderEditor
        disabled={!meetingAllowUpdate}
        canceled={canceled}
        serviceProviderId={`${selectedDesignerId}`}
        serviceProviderName={serviceProviderName}
        serviceProviderOptions={providerOptions}
        onChange={(e) => {
          setSelectedDesignerId(e.target.value)
          setFormData({
            newServiceProviderId: parseInt(e.target.value, 10),
          })
        }}
      />
      <ServiceEditor
        available={isAvailable && meetingAllowUpdate}
        canceled={canceled}
        serviceId={formData.newServiceId && formData.newServiceId.toString()}
        serviceName={
          services.find((service) => service.id === formData.newServiceId)
            ?.heading || 'Unknown meeting type'
        }
        serviceOptions={serviceOptions}
        onChange={(e) =>
          setFormData({
            newServiceId: parseInt(e.target.value, 10),
          })
        }
      />
      <TimeEditor
        canceled={canceled}
        selectedTime={formData.newSelectedTime}
        selectedLength={lengthFromEndTime(
          formData.newSelectedTime,
          formData.newSelectedEndTime
        )}
        timeOptions={timeOptions}
        lengthOptions={lengthOptions}
        closed={closed}
        loading={loading}
        available={isAvailable && meetingAllowUpdate}
        onChangeTime={(e) => {
          const newEndTime = endTimeFromLength(
            e.target.value,
            formData.newSelectedLength
          )
          setFormData({
            newSelectedTime: e.target.value,
            newSelectedEndTime: newEndTime,
          })
        }}
        onChangeLength={(e) =>
          setFormData({
            newSelectedEndTime: endTimeFromLength(
              formData.newSelectedTime,
              parseInt(e.target.value, 10)
            ),
            newSelectedLength: parseInt(e.target.value, 10),
          })
        }
      />
      {canceled && (
        <SectionItem>
          <SectionItemHeading>{t('cancelled-by')}</SectionItemHeading>
          <SectionItemText>
            {(() => {
              const labels = {
                store: t('cancelled-by-store'),
                customer: t('cancelled-by-customer'),
              }
              return labels[cancelledBy] || cancelledBy
            })()}
          </SectionItemText>
        </SectionItem>
      )}
      {canceled && formattedCancellationReason && (
        <SectionItem>
          <SectionItemHeading>{t('cancelled-reasons')}</SectionItemHeading>
          <CancellationReasonText>
            {formattedCancellationReason}
          </CancellationReasonText>
        </SectionItem>
      )}
      {!canceled && (meetingAllowUpdate || meetingAllowCancel) && (
        <WideSectionItem>
          <SectionItemHeading>&nbsp;</SectionItemHeading>
          <ButtonContainer>
            {meetingAllowUpdate && (
              <UpdateButton
                onClick={() =>
                  update(
                    appointmentId,
                    formData,
                    setLoading,
                    setError,
                    onUpdate,
                    timeOptions,
                    lengthOptions,
                    () => {
                      const parent = window.parent
                      if (parent) {
                        parent.postMessage('appointment-updated', '*')
                      }
                      setSuccessModalOpen(true)
                    }
                  ).then(() => resetOldFormData())
                }
                size="small"
                disabled={
                  !isValid(formData, timeOptions, lengthOptions).valid ||
                  closed ||
                  !hasAppointmentChanges(formData)
                }
              >
                {t('update-appointment')}
              </UpdateButton>
            )}
            {meetingAllowCancel &&
              (features.useAmendCancel ? (
                <CancelButton
                  href={`${appBaseUrl}cancel/${appointmentId}/?step=2&store=true`}
                >
                  {t('cancel-appointment')}
                </CancelButton>
              ) : (
                <CancelButton
                  onClick={() => cancel(appointmentId, setLoading, onCancel)}
                >
                  {t('cancel-appointment')}
                </CancelButton>
              ))}
          </ButtonContainer>
        </WideSectionItem>
      )}
      {canceled && <Canceled>{t('appointment-canceled')}</Canceled>}
      {error && <Error>{error}</Error>}
      {successModalOpen && (
        <MessageModal
          setClosed={() => setSuccessModalOpen(false)}
          title={t('update-success-title')}
          body={t('update-success-body')}
          confirmLabel={t('message-modal-confirm')}
        />
      )}
    </>
  )
}

const enhance = compose(
  withStateHandlers(
    (props) => ({
      calendarVisible: false,
      formData: {
        oldSelectedDate: props.selectedDate,
        newSelectedDate: props.selectedDate,
        oldSelectedTime: props.selectedTime,
        newSelectedTime: props.selectedTime,
        oldSelectedEndTime: props.selectedEndTime,
        newSelectedEndTime: props.selectedEndTime,
        newSelectedLength: lengthFromEndTime(
          props.selectedTime,
          props.selectedEndTime
        ),
        oldServiceProviderId: props.serviceProviderId,
        newServiceProviderId: props.serviceProviderId,
        oldStoreId: props.storeId,
        newStoreId: props.storeId,
        oldServiceId: props.serviceId,
        newServiceId: props.serviceId,
      },
      options: {},
    }),
    {
      toggleCalendarVisible:
        ({ calendarVisible }) =>
        () => ({
          calendarVisible: !calendarVisible,
        }),
      setFormData:
        ({ formData }) =>
        (propertyObject) => ({
          formData: { ...formData, ...propertyObject },
        }),
      resetOldFormData:
        ({ formData }) =>
        () => ({
          formData: {
            ...formData,
            oldSelectedDate: formData.newSelectedDate,
            oldSelectedTime: formData.newSelectedTime,
            oldSelectedEndTime: formData.newSelectedEndTime,
            oldServiceProviderId: formData.newServiceProviderId,
            oldStoreId: formData.newStoreId,
            oldServiceId: formData.newServiceId,
          },
        }),
      setOptions:
        ({ options }) =>
        (propertyObject) => ({
          formData: { ...options, ...propertyObject },
        }),
    }
  ),
  useHookAsHoc(
    'timesData',
    ({
      formData,
      locationId,
      appointmentId,
      simplyBookMeSubsystem,
      meetingAllowUpdate,
      canceled,
    }) =>
      useCalendar(
        {
          appointmentId,
          subsystem: simplyBookMeSubsystem,
          locationId,
          serviceProviderId: formData.newServiceProviderId,
          serviceId: formData.newServiceId,
          fromDate: formData.newSelectedDate,
          toDate: formData.newSelectedDate,
          style: 'flexible',
          timeFrame: 30,
          serviceDuration: 30,
          includeHidden: true,
        },
        { skip: canceled || !meetingAllowUpdate || !formData.newServiceId }
      ),
    ({ data }) => data?.[0]
  ),
  useHookAsHoc('serviceProvidersData', ({ formData }) =>
    useServiceProviders(formData.newStoreId, undefined, true, false)
  ),
  withProps(
    ({
      storesData,
      serviceProvidersData,
      timesData,
      formData,
      services,
    }) =>
      getOptions({
        storesData,
        services,
        serviceProvidersData,
        timesData,
        serviceProviderId: formData.newServiceProviderId,
        selectedTime: formData.newSelectedTime,
      })
  )
)

MeetingEditor.propTypes = {
  appointmentId: PropTypes.string,
  onUpdate: PropTypes.func,
  serviceOptions: PropTypes.arrayOf(PropTypes.shape()),
  serviceProviderOptions: PropTypes.arrayOf(PropTypes.shape()),
  storeOptions: PropTypes.arrayOf(PropTypes.shape()),
  timeOptions: PropTypes.arrayOf(PropTypes.shape()),
  lengthOptions: PropTypes.arrayOf(PropTypes.shape()),
  calendarVisible: PropTypes.bool,
  toggleCalendarVisible: PropTypes.func,
  selectedDate: PropTypes.string,
  serviceProviderId: PropTypes.number,
  canceled: PropTypes.bool,
  closed: PropTypes.bool,
  storeId: PropTypes.number,
  formData: PropTypes.shape(),
  setFormData: PropTypes.func,
  onCancel: PropTypes.func,
  services: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  meetingAllowUpdate: PropTypes.bool.isRequired,
  meetingAllowCancel: PropTypes.bool.isRequired,
  cancelledBy: PropTypes.string,
  cancellationReason: PropTypes.string,
}

MeetingEditor.defaultProps = {
  appointmentId: null,
  onCancel: null,
  onUpdate: null,
  serviceOptions: [],
  serviceProviderOptions: [],
  storeOptions: [],
  timeOptions: [],
  lengthOptions: [],
  calendarVisible: false,
  toggleCalendarVisible: null,
  selectedDate: null,
  serviceProviderId: null,
  canceled: null,
  closed: null,
  storeId: null,
  formData: null,
  setFormData: null,
  cancelledBy: null,
  cancellationReason: null,
}

export default enhance(MeetingEditor)
